import type {
  ImportGeneratedLayerAsset,
  ImportPartKey,
  ImportProductSnapshotInput,
  ImportProductSnapshotResult,
} from "./types";
import { SpecsModel } from "../specsModel/SpecsModel";
import { GeneratorSideModel } from "../specsModel/GeneratorSideModel";
import type { RawSpecSide } from "../types";
import type { VersionModel } from "../specsModel/VersionModel";
import { ProductVersionMetadataModel } from "../specsModel/productMetadata/ProductVersionMetadataModel";
import { DEFAULT_PRODUCT_VERSION_STATUS } from "../specsModel/productMetadata/productMetadataEnums";

const PART_LABELS: Record<ImportPartKey, string> = {
  body: "Body",
  laces: "Laces",
  zips: "Zips",
  hand_pockets: "Hand Pockets",
};

const PART_ORDER: ImportPartKey[] = ["body", "laces", "zips", "hand_pockets"];

const IMPORT_PART_ICON_URLS: Record<ImportPartKey, string> = {
  body: "./data/products/hoodie/1.0/body/color/Hoodie__B1.svg",
  laces: "./data/products/hoodie/1.0/hood/laces/Hoodie__Laces.svg",
  zips: "./data/products/hoodieZip/1.0/body/zip/Hoodie_zip_B1.svg",
  hand_pockets:
    "./data/products/hoodie/1.0/body/kangarooPocket/Hoodie__Pocket.svg",
};

const IMPORT_PART_FABRIC_NAMES: Partial<Record<ImportPartKey, string[]>> = {
  body: ["ATFleeceEco515Fabric"],
  laces: ["ATLacesEco515Fabric"],
};

function slugSpecName(value: string) {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part, index) => {
      const lower = part.toLowerCase();
      return index === 0
        ? lower
        : `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join("");
}

function normalizeProductName(productName: string) {
  const normalized = slugSpecName(productName);
  if (!normalized) {
    throw new Error("Product name is required");
  }
  return normalized;
}

function normalizeVersionName(versionName: string) {
  const trimmed = versionName.trim();
  if (!trimmed) {
    throw new Error("Version is required");
  }
  return trimmed;
}

function normalizeIdPrextra(idPrextra: string) {
  const trimmed = idPrextra.trim();
  if (!trimmed) {
    throw new Error("ID Prextra is required");
  }
  return trimmed;
}

function createGeneratorSide(side: RawSpecSide): GeneratorSideModel {
  const model = new GeneratorSideModel();
  model.name = side.name;
  model.zIndex = side.zIndex ?? null;
  model.zIndexHighlight = side.zIndexHighlight ?? null;
  model.imageUrl = side.imageUrl ?? null;
  model.highlightUrl = side.highlightUrl ?? null;
  model.alwaysRedraw = side.alwaysRedraw ?? false;
  if (side.alwaysRedraw !== undefined) {
    model.extra = { alwaysRedraw: side.alwaysRedraw };
  }
  return model;
}

function sortAssetsBySide(assets: ImportGeneratedLayerAsset[]) {
  const sideOrder: Record<ImportGeneratedLayerAsset["side"], number> = {
    front: 0,
    back: 1,
  };
  return [...assets].sort((first, second) => {
    return sideOrder[first.side] - sideOrder[second.side];
  });
}

function addImportLayer(
  version: VersionModel,
  {
    part,
    assets,
    zIndex,
  }: {
    part: ImportPartKey;
    assets: ImportGeneratedLayerAsset[];
    zIndex: number;
  }
): void {
  const firstAsset = assets[0];
  if (!firstAsset) {
    throw new Error("Cannot create import layer without assets");
  }

  const result = version.addLayer(
    {
      partName: part,
      elementName: part,
      styleName: "solid",
      layerName: firstAsset.label,
    },
    {
      layerLabel: firstAsset.label,
      fabricNames: IMPORT_PART_FABRIC_NAMES[firstAsset.part] ?? [],
      required: firstAsset.part === "body",
      credits: 0,
      excludeFromColorCount: false,
      displayable: true,
      displayGroup: "Color",
      displayMethod:
        firstAsset.part === "body" ? "colorPalette" : "colorPicker",
      menuIconUrl: IMPORT_PART_ICON_URLS[firstAsset.part],
    }
  );
  if (!result) {
    throw new Error(`Failed to add import layer "${firstAsset.label}"`);
  }

  result.layer.sides = sortAssetsBySide(assets).map((asset) =>
    createGeneratorSide({
      name: asset.side,
      zIndex,
      zIndexHighlight: zIndex + 1,
      imageUrl: asset.imageUrl,
      highlightUrl: asset.highlightUrl,
      alwaysRedraw: false,
    })
  );
  result.layer.extra = {
    excludeFromColorCount: false,
    techpack: {
      type: "Fabric",
      position: firstAsset.label,
    },
  };
}

function addImportPart(
  version: VersionModel,
  part: ImportPartKey,
  assets: ImportGeneratedLayerAsset[],
  partIndex: number
) {
  const zIndexBase = (partIndex + 1) * 1000;
  const firstAsset = assets[0];
  if (!firstAsset) {
    throw new Error("Cannot create import part without assets");
  }

  if (!version.addPart(part)) {
    throw new Error(`Failed to add import part "${part}"`);
  }
  if (
    !version.updatePart(part, { required: part === "body", inheritFrom: null })
  ) {
    throw new Error(`Failed to update import part "${part}"`);
  }
  const partModel = version.getPart(part);
  if (!partModel) {
    throw new Error(`Failed to resolve import part "${part}"`);
  }

  if (!version.addElement(part, part)) {
    throw new Error(`Failed to add import element for "${part}"`);
  }

  const element = partModel.getElement(part);
  if (!element) {
    throw new Error(`Failed to resolve import element for "${part}"`);
  }
  element.extra = {
    ...element.extra,
    required: part === "body",
  };
  element.setInheritance({ inherit: false });
  element.displayable = true;
  element.menuIconUrl = IMPORT_PART_ICON_URLS[part];
  element.displayMethod = "icon";
  element.displayExtra = {
    displayMethod: "icon",
  };
  element.defaultStyle = "solid";

  const style = element.getStyle("solid");
  if (!style) {
    throw new Error(`Failed to resolve import style for "${part}"`);
  }
  style.extra = {
    ...style.extra,
    credits: 0,
  };
  style.defaultLayer = firstAsset.label;
  style.positionIds = [];
  style.disablingWhenSelected = [];
  style.displayable = true;
  style.menuIconUrl = IMPORT_PART_ICON_URLS[part];
  style.displayMethod = "icon";
  style.displayExtra = {
    displayMethod: "icon",
  };

  addImportLayer(version, {
    part,
    assets,
    zIndex: zIndexBase,
  });

  if (!partModel.setMainElement(part)) {
    throw new Error(`Failed to set main import element for "${part}"`);
  }
}

function createImportVersionMetadata({
  productName,
  idPrextra,
  generatorSides,
}: {
  productName: string;
  idPrextra: string;
  generatorSides: GeneratorSideModel[];
}) {
  const metadata = new ProductVersionMetadataModel()
    .restore({
      status: DEFAULT_PRODUCT_VERSION_STATUS,
      factory: "AT",
      skuJameo: productName,
      skuPrextra: idPrextra,
      productLine: "CLASSIC",
      productSex: "Unisex",
      deliveryTime: {
        minWeeks: 2,
        maxWeeks: 3,
      },
      pricing: {
        freeCredits: 0,
        minQty: 1,
        pricingCurrency: {
          cad: {
            carbonNeutralityFee: 0,
            priceOfExtraCredit: 0,
            priceForExtraSize: 0,
            pricingRange: [],
            pricePerSize: [],
          },
        },
        chargeExtraSize: [],
      },
      availableImageSides: generatorSides.map((side) => ({ name: side.name })),
      specsImport: {
        createdBy: "specs-editor-import-page",
        notes: [
          "AI-assisted first pass. Clean up names, layers, and positions in Speculoos.",
        ],
      },
    })
    .save();

  return {
    labels: [],
    packaging: {},
    shipping: [],
    sizes: [],
    availableDecorationTypes: [],
    ...metadata,
  };
}

function createImportProductModel({
  productName,
  displayName,
  idPrextra,
  versionName,
  generatorSides,
}: {
  productName: string;
  displayName: string;
  idPrextra: string;
  versionName: string;
  generatorSides: GeneratorSideModel[];
}) {
  const model = new SpecsModel();
  const version = model.addVersion(versionName);
  if (!version) {
    throw new Error(`Failed to add import version "${versionName}"`);
  }

  version.setRawVersionMetadata(
    createImportVersionMetadata({
      productName,
      idPrextra,
      generatorSides,
    })
  );
  version.positions = [];
  version.positionGroups = [];
  version.generatorSides = generatorSides;

  model.product.name = productName;
  model.product.versions = [version];
  model.product.extra = {
    displayName,
    defaultVersion: version.name,
    skuPrextra: idPrextra,
    display: {
      menuIconUrl: "",
      displayMethod: "none",
      displayable: true,
    },
    specsImport: {
      createdBy: "specs-editor-import-page",
      notes: [
        "AI-assisted first pass. Clean up names, layers, and positions in Speculoos.",
      ],
    },
  };

  return model;
}

export function buildImportProductSnapshot({
  productName,
  displayName,
  versionName,
  idPrextra,
  generatedLayers,
  globalSides,
}: ImportProductSnapshotInput): ImportProductSnapshotResult {
  const normalizedProductName = normalizeProductName(productName);
  const normalizedVersionName = normalizeVersionName(versionName);
  const normalizedIdPrextra = normalizeIdPrextra(idPrextra);
  const layersByPart = new Map<ImportPartKey, ImportGeneratedLayerAsset[]>();

  for (const layer of generatedLayers) {
    const current = layersByPart.get(layer.part) ?? [];
    layersByPart.set(layer.part, [...current, layer]);
  }

  if (!layersByPart.has("body")) {
    throw new Error("Import requires a generated body layer");
  }

  const generatorSides = (["front", "back"] as const).flatMap((side) => {
    const globalSide = globalSides[side];
    if (!globalSide) {
      return [];
    }
    return [
      createGeneratorSide({
        name: side,
        zIndex: 150001,
        zIndexHighlight: 150000,
        imageUrl: globalSide.imageUrl,
        highlightUrl: globalSide.highlightUrl,
      }),
    ];
  });

  const model = createImportProductModel({
    productName: normalizedProductName,
    displayName: displayName.trim() || normalizedProductName,
    idPrextra: normalizedIdPrextra,
    versionName: normalizedVersionName,
    generatorSides,
  });
  const version = model.getVersion(normalizedVersionName);
  if (!version) {
    throw new Error(
      `Failed to resolve import version "${normalizedVersionName}"`
    );
  }

  PART_ORDER.forEach((part, index) => {
    const assets = layersByPart.get(part) ?? [];
    if (assets.length > 0) {
      addImportPart(version, part, assets, index);
    }
  });

  return {
    product: model.save(),
  };
}

export function getImportPartLabel(part: ImportPartKey) {
  return PART_LABELS[part];
}

export function getEnabledImportParts(parts: Record<ImportPartKey, boolean>) {
  return PART_ORDER.filter((part) => parts[part]);
}
